<template>
	<app-page>
		<app-heading title="PriceLabs Sync Status">
			<template v-slot:right>
				<app-button
					outlined
					small
					color="secondary"
					icon="fa-sync"
					@click="refresh = true"
				>Refresh</app-button>
			</template>
		</app-heading>
		<app-filter :value="hasFilters" @click="applyFilters()">
			<app-row no-padding>
				<app-cell cols="6" sm="3">
					<input-select
						v-model="filters.resort"
						label="Resort"
						:items="resorts"
						clearable
						dense
					></input-select>
				</app-cell>
				<app-cell cols="6" sm="3">
					<input-text
						v-model="filters.unit"
						label="Unit ID"
						clearable
						dense
					></input-text>
				</app-cell>
			</app-row>
		</app-filter>
		<app-row>
			<app-cell>
				<app-data-table
					:items="entries"
					:total="count"
					:refresh.sync="refresh"
					:headers="headers"
					:sort-by="'last_smart_price_sync'"
					:sort-desc="true"
					@update="fetchStatuses"
				>
					<template v-slot:unit="{ item }">
						<router-link
							v-if="item.resort"
							:to="{ name: 'studeo-resort-unit', params: { resort: String(item.resort), unit: String(item.unit) } }"
						>
							{{ getUnitCode(item.unit) || item.unit }}
						</router-link>
						<span v-else>{{ getUnitCode(item.unit) || item.unit }}</span>
					</template>
					<template v-slot:summary="{ item }">
						<span :class="summaryClass(item)">{{ summaryLabel(item) }}</span>
					</template>
					<template v-slot:listings="{ item }">
						<span :class="statusClass(hopStatus(item, 'listings'))" :title="hopReason(item, 'listings')">
							{{ hopLabel(item, 'listings') }}
						</span>
						<br>
						<i class="grey--text caption">{{ formatDate(hopAt(item, 'listings', 'last_listings_sync')) }}</i>
					</template>
					<template v-slot:calendar="{ item }">
						<span :class="statusClass(hopStatus(item, 'calendar'))" :title="hopReason(item, 'calendar')">
							{{ hopLabel(item, 'calendar') }}
						</span>
						<br>
						<i class="grey--text caption">{{ formatDate(hopAt(item, 'calendar', 'last_calendar_sync')) }}</i>
					</template>
					<template v-slot:prices="{ item }">
						<span :class="statusClass(hopStatus(item, 'prices'))" :title="hopReason(item, 'prices')">
							{{ hopLabel(item, 'prices') }}
						</span>
						<br>
						<i class="grey--text caption">{{ formatDate(hopAt(item, 'prices', 'last_smart_price_sync')) }}</i>
					</template>
					<template v-slot:reservations="{ item }">
						<span :class="statusClass(hopStatus(item, 'reservations'))" :title="hopReason(item, 'reservations')">
							{{ hopLabel(item, 'reservations') }}
						</span>
						<br>
						<i class="grey--text caption">{{ formatDate(hopAt(item, 'reservations')) }}</i>
					</template>
					<template v-slot:actions="{ item }">
						<app-button
							color="secondary"
							table
							@click="onDetails(item)"
						>
							Details
						</app-button>
					</template>
				</app-data-table>
			</app-cell>
		</app-row>

		<app-dialog
			v-model="details.show"
			max-width="720"
			:title="details.title"
		>
			<app-dialog-body>
				<app-row v-if="details.item">
					<app-cell cols="12">
						<div class="mb-2"><b>Hops</b> — {{ summaryLabel(details.item) }}</div>
						<app-json :data="hopsForDetails(details.item)" open />
					</app-cell>
					<app-cell cols="12">
						<div class="mb-2"><b>Derived last_error</b></div>
						<app-json v-if="details.item.last_error" :data="details.item.last_error" open />
						<i v-else class="grey--text">None (from failing hops only; a later hop success no longer clears this)</i>
					</app-cell>
					<app-cell
						v-if="details.item.details && Object.keys(details.item.details).length"
						cols="12"
					>
						<div class="mb-2"><b>Legacy details</b></div>
						<app-json :data="details.item.details" open />
					</app-cell>
				</app-row>
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					flat
					@click="details.show = false"
				>Close</app-button>
			</app-dialog-footer>
		</app-dialog>
	</app-page>
</template>

<script>
import filters from '@/mixins/filters'
import { mapSelectOptions, mapFields } from '@/utils/maps'

const HOPS = [
	{ key: 'listings', legacy: 'last_listings_sync' },
	{ key: 'calendar', legacy: 'last_calendar_sync' },
	{ key: 'prices', legacy: 'last_smart_price_sync' },
	{ key: 'reservations', legacy: null },
]

export default {
	mixins: [filters],
	async created() {
		const resortsData = await this.$ravello.request()
			.v(2)
			.to('resorts')
			.field(['name'])
			.limit(1000)
			.get()
		this.resorts = mapSelectOptions(resortsData)
		this.resortsMap = mapFields(resortsData, 'name')
	},
	data() {
		return {
			entries: [],
			resorts: [],
			resortsMap: {},
			unitsMap: {},
			count: -1,
			details: {
				show: false,
				title: '',
				item: null,
			},
			refresh: false,
			headers: [{
				text: 'Unit',
				value: 'unit',
				sortable: true,
			}, {
				text: 'Status',
				value: 'summary',
				sortable: false,
			}, {
				text: 'Listings',
				value: 'listings',
				sortable: false,
			}, {
				text: 'Calendar',
				value: 'calendar',
				sortable: false,
			}, {
				text: 'Prices',
				value: 'prices',
				sortable: false,
			}, {
				text: 'Reservations',
				value: 'reservations',
				sortable: false,
			}, {
				text: 'Actions',
				value: 'actions',
				sortable: false,
				align: 'right',
			}],
			filters: {
				resort: '',
				unit: '',
			},
		}
	},
	methods: {
		formatDate(val) {
			if (!val) return '—'
			const d = new Date(val)
			return d.toLocaleString()
		},
		hopOf(item, key) {
			return item && item[key] && item[key].status ? item[key] : null
		},
		hopStatus(item, key) {
			const hop = this.hopOf(item, key)
			return hop ? hop.status : 'unknown'
		},
		hopLabel(item, key) {
			const status = this.hopStatus(item, key)
			return status === 'unknown' ? '—' : status
		},
		hopReason(item, key) {
			const hop = this.hopOf(item, key)
			return hop && hop.error && hop.error.message ? hop.error.message : ''
		},
		hopAt(item, key, legacy) {
			const hop = this.hopOf(item, key)
			if (hop && hop.at) return hop.at
			return legacy ? item[legacy] : null
		},
		summaryCounts(item) {
			if (item.summary && item.summary.total) {
				return item.summary
			}
			const counts = {
				ok: 0, failed: 0, skipped: 0, total: 0,
			}
			HOPS.forEach(({ key }) => {
				const status = this.hopStatus(item, key)
				if (status === 'unknown') return
				counts.total += 1
				if (status === 'ok') counts.ok += 1
				else if (status === 'skipped') counts.skipped += 1
				else counts.failed += 1
			})
			return counts
		},
		summaryLabel(item) {
			const s = this.summaryCounts(item)
			if (!s.total) return '—'
			return `${s.ok}/${s.total}`
		},
		summaryClass(item) {
			const s = this.summaryCounts(item)
			if (!s.total) return 'grey--text'
			if (s.failed) return 'error--text'
			if (s.skipped) return 'warning--text text--darken-2'
			return 'success--text'
		},
		statusClass(status) {
			if (status === 'ok') return 'success--text'
			if (status === 'skipped' || status === 'running') return 'warning--text text--darken-2'
			if (status === 'error') return 'error--text'
			return 'grey--text'
		},
		hopsForDetails(item) {
			const out = {}
			HOPS.forEach(({ key, legacy }) => {
				out[key] = this.hopOf(item, key) || {
					status: 'unknown',
					at: legacy ? item[legacy] : null,
					error: null,
					details: null,
				}
			})
			return out
		},
		getUnitCode(unitId) {
			return this.unitsMap[String(unitId)]
		},
		async fetchStatuses(pagination) {
			const request = this.$ravello.request()
				.v(2)
				.to('pricelabsstatuses')
				.fromPagination(pagination)

			if (this.appliedFilters.resort) {
				request.param('resort', this.appliedFilters.resort)
			}
			if (this.appliedFilters.unit) {
				request.param('unit', this.appliedFilters.unit)
			}

			try {
				this.entries = await request.get()
				if (this.count === -1) {
					const countResult = await request.count()
					this.count = countResult.count
				}
				await this.updateUnitsMap()
			} catch (err) {
				this.$notifications.pushAlert('error', err.data ? err.data.message : err.message)
			}
		},
		async updateUnitsMap() {
			const resortIds = [...new Set(this.entries.filter(e => e.resort).map(e => String(e.resort)))]
			const results = await Promise.all(resortIds.map(async (resortId) => {
				try {
					return await this.$ravello.request()
						.v(2)
						.to('resorts')
						.to(resortId)
						.to('units')
						.limit(500)
						.field(['code'])
						.get()
				} catch (e) {
					return []
				}
			}))
			results.forEach((units) => {
				(Array.isArray(units) ? units : []).forEach((u) => {
					this.unitsMap[String(u._id)] = u.code
				})
			})
		},
		onDetails(item) {
			this.details.item = item
			this.details.title = `PriceLabs Status — ${this.getUnitCode(item.unit) || item.unit}`
			this.details.show = true
		},
	},
}
</script>
